import { useFacilityModelAccess } from 'features/facilityModel/access'
import {
  buildSubjectLinkIndex,
  type SubjectLinkTarget,
} from 'features/facilityModel/subjectLinkify'
import { useSubjectsIndex } from 'features/facilityModel/useSubjectsIndex'
import { useFimSubjectStats } from 'features/fim'
import { useEffect, useMemo, useState } from 'react'

/** Fallback when `requestIdleCallback` is unavailable (Safari). */
const IDLE_FALLBACK_MS = 250

/**
 * The subject name/alias → uid dictionary doc-chat linkification scans
 * against, built from the same whole-run instant-search index the Facility
 * Model catalog uses. Idle-armed after first paint so a chat response never
 * blocks on it, and gated on `canView` (permission + the Longridge release
 * gate together) so it never fetches for a user or site without Facility
 * Model access. Every non-ready state — idle, loading, error, or skipped
 * (run too large for the in-memory index) — resolves to `undefined`, never
 * an error a chat surface would have to render.
 */
export function useSubjectLinkIndex(
  siteId: string | undefined
): ReadonlyMap<string, SubjectLinkTarget> | undefined {
  const { canView } = useFacilityModelAccess(siteId)

  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (armed) return
    const idle: (cb: () => void) => number =
      typeof window.requestIdleCallback === 'function'
        ? (cb) => window.requestIdleCallback(cb)
        : (cb) => window.setTimeout(cb, IDLE_FALLBACK_MS)
    const handle = idle(() => setArmed(true))
    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(handle)
      } else {
        window.clearTimeout(handle)
      }
    }
  }, [armed])

  const { stats } = useFimSubjectStats({
    siteID: siteId,
    enabled: canView && armed,
  })

  const subjectsIndex = useSubjectsIndex({
    siteID: siteId,
    enabled: canView && armed,
    total: stats?.total ?? null,
  })

  return useMemo(
    () =>
      subjectsIndex.status === 'ready' && subjectsIndex.rows
        ? buildSubjectLinkIndex(subjectsIndex.rows)
        : undefined,
    [subjectsIndex.status, subjectsIndex.rows]
  )
}
